import React, { useState } from 'react';
import { Modal } from 'antd';
import { 
  RobotOutlined, 
  CustomerServiceOutlined, 
  BulbOutlined, 
  MessageOutlined,
  SoundOutlined,
  MutedOutlined
} from '@ant-design/icons';

const ChatPreview = ({ 
  visible, 
  onClose, 
  design, 
  behavior, 
  model,
  modelParams,
  systemPrompt 
}) => {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: behavior.initialMessage }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [soundEnabled, setSoundEnabled] = useState(true);

  const handleSend = () => {
    if (!inputValue.trim()) return;
    setMessages(prev => [...prev, { role: 'user', content: inputValue }]);
    setInputValue('');
    setTimeout(() => {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "This is a preview. In the live chat, I'll respond based on your configuration and knowledge base." 
      }]);
    }, 1000);
  };

  const getBotIcon = () => {
    switch (design.botIcon) {
      case 'robot':
        return <RobotOutlined style={{ fontSize: '20px' }} />;
      case 'assistant':
        return <CustomerServiceOutlined style={{ fontSize: '20px' }} />;
      case 'brain':
        return <BulbOutlined style={{ fontSize: '20px' }} />;
      case 'chat':
        return <MessageOutlined style={{ fontSize: '20px' }} />;
      default:
        return <RobotOutlined style={{ fontSize: '20px' }} />;
    }
  };

  const getStyles = () => {
    const baseStyles = {
      container: {
        width: design.dimensions.width,
        height: design.dimensions.height,
        fontFamily: design.font?.family || 'Inter, system-ui, sans-serif',
        fontSize: design.font?.size || '14px',
        backgroundColor: design.theme === 'dark' ? '#1f1f1f' : '#ffffff',
        color: design.theme === 'dark' ? '#ffffff' : '#000000',
        '--chat-border-radius': `${design.dimensions.borderRadius}px`
      },
      header: {
        fontSize: design.font?.headerSize || '16px',
        borderTopLeftRadius: `${design.dimensions.borderRadius}px`,
        borderTopRightRadius: `${design.dimensions.borderRadius}px`,
      }
    };

    if (design.theme === 'custom') {
      return {
        ...baseStyles,
        container: {
          ...baseStyles.container,
          backgroundColor: design.colors.background,
          color: design.colors.text,
          '--chat-primary': design.colors.primary,
          '--chat-bg': design.colors.background,
          '--chat-text': design.colors.text,
          '--chat-secondary': design.colors.secondary,
        }
      };
    }

    return baseStyles;
  };

  const styles = getStyles();

  return (
    <Modal
    title="Chat Preview"
    open={visible}
    onCancel={onClose}
    width={Math.min(design.dimensions.width + 40, window.innerWidth - 40)} // Add max width
    footer={null}
    bodyStyle={{ padding: 0 }}
    style={{ 
      maxWidth: '95vw',
      margin: '0 auto'
    }}
  >
      <div 
        className={`wp-ai-workflows-chat-widget ${design.theme}`}
        style={styles.container}
      >
        <div className="chat-header" style={styles.header}>
          <div className="header-content">
            {getBotIcon()}
            <span className="bot-name">{design.botName || 'AI Assistant'}</span>
          </div>
          <div className="header-actions">
            <button 
              className="sound-toggle"
              onClick={() => setSoundEnabled(!soundEnabled)}
              aria-label={soundEnabled ? 'Mute sound' : 'Unmute sound'}
            >
              {soundEnabled ? <SoundOutlined /> : <MutedOutlined />}
            </button>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="chat-messages">
          {messages.map((message, index) => (
            <div key={index} className={`chat-message ${message.role}`}>
              <div className="message-content">
                {message.content}
              </div>
            </div>
          ))}
        </div>

        <div className="chat-input">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={behavior.placeholderText}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
          />
          <button onClick={handleSend}>Send</button>
        </div>

        <div className="powered-by">
          <a 
            href="https://bit.ly/3CEdP35"
            target="_blank"
            rel="noopener noreferrer"
            title="Create your own AI chatbot"
          >
            Powered by AI Workflow Automation Plugin
          </a>
        </div>
      </div>

      <style>{`
        .wp-ai-workflows-chat-widget {
          display: flex;
          flex-direction: column;
          overflow: hidden;
          position: relative;
          border-radius: var(--chat-border-radius);
        }

        .wp-ai-workflows-chat-widget.dark {
          background-color: #1f1f1f;
          color: #ffffff;
        }

        .chat-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background: var(--chat-primary, #1677ff);
          color: white;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .bot-name {
          font-weight: 500;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .sound-toggle,
        .close-button {
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.8);
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s ease;
        }

        .sound-toggle:hover,
        .close-button:hover {
          color: #ffffff;
        }

        .chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          background-color: var(--chat-bg, inherit);
        }

        .chat-message {
          display: flex;
          margin-bottom: 8px;
        }

        .chat-message.user {
          justify-content: flex-end;
        }

        .message-content {
          padding: 8px 12px;
          border-radius: 12px;
          max-width: 80%;
          word-wrap: break-word;
        }

        .chat-message.user .message-content {
          background: var(--chat-primary, #1677ff);
          color: white;
        }

        .chat-message.assistant .message-content {
          background: var(--chat-secondary, #f5f5f5);
          color: var(--chat-text, inherit);
        }

        .chat-input {
          padding: 16px;
          display: flex;
          gap: 8px;
          border-top: 1px solid var(--chat-secondary, #f0f0f0);
          background-color: var(--chat-bg, inherit);
        }

        .chat-input input {
          flex: 1;
          padding: 8px 12px;
          border: 1px solid var(--chat-secondary, #f0f0f0);
          border-radius: 6px;
          background: var(--chat-bg, #ffffff);
          color: var(--chat-text, inherit);
        }

        .chat-input button {
          padding: 8px 16px;
          background: var(--chat-primary, #1677ff);
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: opacity 0.2s ease;
        }

        .chat-input button:hover {
          opacity: 0.9;
        }

        .powered-by {
          text-align: center;
          padding: 8px;
          font-size: 12px;
          opacity: 0.7;
          background-color: var(--chat-bg, inherit);
          color: var(--chat-text, inherit);
        }

        .powered-by a {
          color: inherit;
          text-decoration: none;
        }

        .powered-by a:hover {
          text-decoration: underline;
        }

        .dark .powered-by {
          opacity: 0.5;
        }
      `}</style>
    </Modal>
  );
};

export default ChatPreview;